import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import ProductNav from '../../components/feature/ProductNav';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

const faqs = [
  {
    q: 'What is an annuity and how does it work?',
    a: 'An annuity is a contract between you and an insurance company. You make a lump-sum payment or series of payments, and in return the insurance company provides regular disbursements beginning either immediately or at some future date. It\'s primarily used to create a reliable income stream during retirement.',
  },
  {
    q: 'What is the difference between a fixed and indexed annuity?',
    a: 'A fixed annuity pays a guaranteed interest rate on your premium. An indexed annuity links your interest crediting to a stock market index (like the S&P 500) while protecting you from market losses — you can earn more than a fixed annuity in strong markets with no downside risk to principal.',
  },
  {
    q: 'Are annuities tax-deferred?',
    a: 'Yes. Annuities grow tax-deferred, meaning you don\'t pay taxes on growth until you withdraw. This is especially valuable for high earners who have maxed out other tax-advantaged accounts like 401(k)s and IRAs.',
  },
  {
    q: 'Can I lose money in an annuity?',
    a: 'With fixed and indexed annuities, your principal is protected from market losses. With variable annuities, your investment is subject to market risk. The vast majority of annuities we place at Hoosier Life Insurance are fixed or indexed — principal-protected products.',
  },
  {
    q: 'When can I access my annuity funds?',
    a: 'Most annuities have a surrender period (typically 5–10 years) during which early withdrawals may incur a surrender charge. However, most annuities allow penalty-free withdrawals of 10% per year. After the surrender period, you have full access to your funds.',
  },
  {
    q: 'How much does an annuity cost?',
    a: 'Unlike life insurance, there are no monthly premiums for most annuities. You fund an annuity with a lump sum (minimum typically $5,000–$25,000) and the insurance company manages the rest. Fixed and indexed annuities have no direct fees deducted from your account value.',
  },
];

const annuityTypes = [
  {
    title: 'Fixed Annuity',
    icon: 'ri-safe-2-line',
    badge: 'Guaranteed Rate',
    badgeColor: 'bg-green-500',
    desc: 'Earns a guaranteed fixed interest rate for a set period. Similar to a CD, but with tax-deferred growth and often higher rates.',
    features: ['Guaranteed interest rate', 'Principal protected', 'Tax-deferred growth', 'Simple, predictable'],
    bestFor: 'Conservative savers wanting certainty',
  },
  {
    title: 'Indexed Annuity',
    icon: 'ri-line-chart-line',
    badge: 'Most Popular',
    badgeColor: 'bg-[#D97642]',
    desc: 'Interest tied to market index performance (S&P 500) with a 0% floor — you gain when markets rise but never lose principal when markets fall.',
    features: ['Market-linked upside', '0% floor on bad years', 'Tax-deferred growth', 'Higher potential than fixed'],
    bestFor: 'Moderate savers wanting growth with protection',
  },
  {
    title: 'Multi-Year Guaranteed (MYGA)',
    icon: 'ri-calendar-check-line',
    badge: 'Fixed Term',
    badgeColor: 'bg-[#1A3A52]',
    desc: 'Locks in a guaranteed interest rate for a multi-year period (2–10 years). Often offers the highest guaranteed rates available.',
    features: ['Highest guaranteed rates', 'Set term (2–10 yrs)', 'Simple and transparent', 'Tax-deferred growth'],
    bestFor: 'Savers wanting maximum guaranteed return',
  },
];

export default function AnnuitiesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Annuities Indianapolis | Retirement Income | Hoosier Life';

    const metaTags: { name?: string; property?: string; content: string }[] = [
      {
        name: 'description',
        content:
          'Fixed & indexed annuities in Indianapolis. Tax-deferred growth, zero market risk, guaranteed income. Local specialist. Call (317) 648-2983.',
      },
      { name: 'keywords', content: 'annuities Indianapolis, fixed annuity Indiana, indexed annuity Indiana, retirement income Indianapolis, annuity specialist Indiana' },
      { property: 'og:title', content: 'Annuities Indianapolis | Retirement Income | Hoosier Life' },
      { property: 'og:description', content: 'Fixed & indexed annuities in Indianapolis. Tax-deferred growth, zero market risk, guaranteed income. Local specialist.' },
      { property: 'og:url', content: `${SITE_URL}/annuities` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/8b436d11081b9d93e75fbd4c3d01d52e.jpg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Annuities Indianapolis | Retirement Income | Hoosier Life' },
      { name: 'twitter:description', content: 'Fixed & indexed annuities in Indianapolis. Tax-deferred growth, zero market risk, guaranteed income. Local specialist.' },
      { name: 'twitter:image', content: 'https://readdy.ai/api/search-image?query=happy%20retired%20senior%20couple%20enjoying%20outdoor%20lifestyle%20in%20beautiful%20Indiana%20garden%20retirement%20financial%20security%20wealth%20planning%20professional%20lifestyle%20photography%20warm%20natural%20light%20golden%20hour&width=1200&height=630&seq=annuities-og-image-v2&orientation=landscape' },
    ];

    const created: HTMLElement[] = [];

    metaTags.forEach((tag) => {
      const key = tag.name || tag.property || '';
      const attr = tag.name ? 'name' : 'property';
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', tag.content);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      created.push(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/annuities`);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${SITE_URL}/annuities#service`,
        name: 'Annuities Indianapolis Indiana',
        description: 'Fixed and indexed annuities for Indiana retirees. Tax-deferred growth, principal protection, and guaranteed retirement income.',
        provider: { '@id': `${SITE_URL}/#organization` },
        areaServed: { '@type': 'State', name: 'Indiana' },
        serviceType: 'Annuity',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '82',
          bestRating: '5',
          worstRating: '1',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/annuities#webpage`,
        url: `${SITE_URL}/annuities`,
        name: 'Annuities Indianapolis | Retirement Income | Hoosier Life',
        description: 'Fixed & indexed annuities in Indianapolis. Tax-deferred growth, zero market risk, guaranteed income. Local specialist. Call (317) 648-2983.',
        inLanguage: 'en-US',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        dateModified: new Date().toISOString(),
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['h1', 'h2:first-of-type', '.key-takeaways-heading']
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Annuities', item: `${SITE_URL}/annuities` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ];

    const scripts: HTMLScriptElement[] = [];
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.id = `schema-annuities-${i}`;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    // InsuranceProduct schema
    const ipSchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceProduct',
      '@id': `${SITE_URL}/annuities#insurance-product`,
      name: 'Annuities — Retirement Income Insurance',
      description: 'Fixed and indexed annuity products providing guaranteed retirement income for Indiana individuals and seniors. Tax-deferred growth with no annual contribution limits. Protection against outliving your savings.',
      url: `${SITE_URL}/annuities`,
      category: 'Annuity',
      brand: { '@type': 'Brand', name: 'Hoosier Life Insurance' },
      provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization` },
      isRelatedTo: { '@type': 'Organization', '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'State', name: 'Indiana' },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'USD',
          description: 'Lump sum or flexible premium payments; no contribution caps',
        },
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/annuities`,
      },
    };
    const ipScriptAnn = document.createElement('script');
    ipScriptAnn.id = 'schema-annuities-insurance-product';
    ipScriptAnn.type = 'application/ld+json';
    ipScriptAnn.textContent = JSON.stringify(ipSchema);
    document.head.appendChild(ipScriptAnn);

    return () => {
      created.forEach((el) => { try { document.head.removeChild(el); } catch (e) { /* ignore */ } });
      scripts.forEach((s) => { try { document.head.removeChild(s); } catch (e) { /* ignore */ } });
      const ipEl = document.getElementById('schema-annuities-insurance-product');
      if (ipEl) ipEl.remove();
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <ProductNav />

      <main className="flex-grow">
        {/* Hero */}
        <section
          className="relative pt-28 pb-20 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url("https://readdy.ai/api/search-image?query=happy%20retired%20senior%20couple%20enjoying%20outdoor%20lifestyle%20in%20beautiful%20garden%20Indiana%20retirement%20financial%20security%20wealth%20planning%20professional%20lifestyle%20photography%20warm%20natural%20light&width=1440&height=700&seq=annuities-hero-1&orientation=landscape")',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/90 via-[#1A3A52]/70 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-white/70 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span>/</span>
              <span className="text-white">Annuities</span>
            </nav>
            <div className="max-w-2xl">
              <span className="inline-block bg-[#D97642] text-white text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                Principal-Protected Growth
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
                Guaranteed Retirement Income with Annuities — Indiana Residents
              </h1>
              <p className="text-xl text-white/85 mb-8 leading-relaxed">
                Think of an annuity as a paycheck you literally cannot outlive. You put in a lump sum — it grows tax-free — and then it pays you every single month for as long as you&apos;re alive. If the stock market crashes, your money stays safe. Starting at $5,000.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/book-appointment"
                  className="px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#c26535] transition-colors text-center whitespace-nowrap"
                >
                  Schedule Free Strategy Call
                </Link>
                <Link
                  to="/instant-quote"
                  className="px-8 py-4 bg-white/10 text-white font-bold rounded-lg border border-white/30 hover:bg-white/20 transition-colors text-center whitespace-nowrap"
                >
                  Get Quote
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-6">
                {['Tax-Deferred Growth', '0% Loss Floor', 'Principal Protected', 'Market-Linked Upside'].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-white/90 text-sm">
                    <i className="ri-checkbox-circle-fill text-[#D97642]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Key Takeaways — AI SEO Signal */}
        <section className="py-8 bg-[#1A3A52]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
              </div>
              <div className="flex-1">
                <h2 className="text-white font-bold text-lg mb-3">Key Takeaways: Annuities for Indiana Retirees</h2>
                <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                  {[
                    'Fixed and indexed annuities protect your principal — even a market crash CANNOT reduce what you put in',
                    'Your money grows tax-free inside the annuity — you only pay taxes when you withdraw in retirement',
                    'Indexed annuities earn when the S&P 500 goes up (cap 10–12%) but earn nothing when it drops — zero loss',
                    'Add an income rider and get a guaranteed monthly paycheck for life — income you literally cannot outlive',
                    'No annual contribution limits — unlike 401(k)s and IRAs, you can put in as much as you want',
                    'We compare annuity rates from 15+ carriers — American Equity, North American, Nationwide & more',
                  ].map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                      <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 text-white/50 text-xs">Last reviewed March 2025 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Annuities */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Why Indiana Families Choose Annuities</h2>
              <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
                A 401(k) runs out. Social Security might not be enough. An annuity solves both problems — it pays you every month for life, no matter how long you live or what the market does.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: 'Principal Protection',
                  desc: 'Fixed and indexed annuities guarantee your principal. Market crashes can never reduce the money you put in.',
                },
                {
                  icon: 'ri-tax-line',
                  title: 'Tax-Deferred Growth',
                  desc: 'Your money grows without current income taxes. You only pay taxes when you withdraw, typically during retirement when your tax rate is lower.',
                },
                {
                  icon: 'ri-funds-line',
                  title: 'Guaranteed Lifetime Income',
                  desc: 'Add an income rider and you can never outlive your money — guaranteed monthly payments for as long as you live.',
                },
                {
                  icon: 'ri-line-chart-line',
                  title: 'Market Participation',
                  desc: "Indexed annuities let you participate in stock market gains without the downside risk. You earn in bull markets and lose nothing in bear markets.",
                },
                {
                  icon: 'ri-bank-line',
                  title: 'No Contribution Limits',
                  desc: "Unlike IRAs and 401(k)s, there are no annual limits on how much you can put into an annuity — ideal if you've maxed out other accounts.",
                },
                {
                  icon: 'ri-user-heart-line',
                  title: 'Death Benefit',
                  desc: 'Most annuities include a death benefit, ensuring any remaining value passes to your beneficiaries without going through probate.',
                },
              ].map((item) => (
                <div key={item.title} className="bg-[#F9F7F4] rounded-xl p-6 border border-gray-100">
                  <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-2xl text-[#1A3A52]`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Annuity Types */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Types of Annuities We Offer</h2>
              <p className="text-lg text-[#4A5568] max-w-2xl mx-auto">
                We work with multiple top-rated carriers to find the annuity that matches your goals and timeline.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {annuityTypes.map((plan) => (
                <div key={plan.title} className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col">
                  <div className="w-14 h-14 bg-[#1A3A52]/10 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${plan.icon} text-3xl text-[#1A3A52]`} />
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-[#1A3A52]">{plan.title}</h3>
                    <span className={`${plan.badgeColor} text-white text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap`}>{plan.badge}</span>
                  </div>
                  <p className="text-[#4A5568] text-sm mb-4 leading-relaxed">{plan.desc}</p>
                  <ul className="space-y-2 mb-4">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-[#4A5568]">
                        <i className="ri-checkbox-circle-fill text-[#D97642]" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto pt-4 border-t border-gray-100">
                    <p className="text-xs font-semibold text-[#4A5568]">Best For:</p>
                    <p className="text-sm text-[#1A3A52] font-medium">{plan.bestFor}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Annuities vs. Other Retirement Accounts</h2>
            </div>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-4 py-4 text-left font-semibold">Feature</th>
                    <th className="px-4 py-4 text-center font-semibold">Annuity</th>
                    <th className="px-4 py-4 text-center font-semibold">401(k) / IRA</th>
                    <th className="px-4 py-4 text-center font-semibold">Bank CD</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Contribution Limits', annuity: 'None', retirement: 'Yes (annual limits)', cd: 'None' },
                    { label: 'Tax-Deferred Growth', annuity: '✓ Yes', retirement: '✓ Yes', cd: '✗ No' },
                    { label: 'Principal Protection', annuity: '✓ Yes (fixed/indexed)', retirement: '✗ Market risk', cd: '✓ FDIC-insured' },
                    { label: 'Market Participation', annuity: '✓ Indexed options', retirement: '✓ Yes', cd: '✗ No' },
                    { label: 'Guaranteed Lifetime Income', annuity: '✓ With income rider', retirement: '✗ No guarantee', cd: '✗ No' },
                    { label: 'Probate Avoidance', annuity: '✓ Beneficiary direct', retirement: '✓ Yes', cd: '✗ Goes through estate' },
                  ].map((row, i) => (
                    <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                      <td className="px-4 py-3 font-medium text-[#1A3A52]">{row.label}</td>
                      <td className="px-4 py-3 text-center text-[#4A5568]">{row.annuity}</td>
                      <td className="px-4 py-3 text-center text-[#4A5568]">{row.retirement}</td>
                      <td className="px-4 py-3 text-center text-[#4A5568]">{row.cd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Indiana Retirees Trust Hoosier Life</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Gerald & Carol B.',
                  city: 'Carmel, IN',
                  text: 'We rolled over our 401k into an indexed annuity after retirement and earned 11% in our first year while our friends with market investments lost 18%. Best financial decision we ever made.',
                  stars: 5,
                },
                {
                  name: 'David K.',
                  city: 'Indianapolis, IN',
                  text: "Benjamin walked us through every annuity option and explained exactly how the indexed crediting works. No pressure at all — he helped us understand what was right for OUR situation. Outstanding service.",
                  stars: 5,
                },
                {
                  name: 'Margaret S.',
                  city: 'Noblesville, IN',
                  text: 'I was worried about outliving my savings. Adding a lifetime income rider to my annuity means I get a guaranteed check every month no matter how long I live. That peace of mind is priceless.',
                  stars: 5,
                },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: t.stars }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#D97642]" />
                    ))}
                  </div>
                  <p className="text-[#4A5568] text-sm leading-relaxed mb-4">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A3A52] text-sm">{t.name}</p>
                    <p className="text-[#4A5568] text-xs">{t.city}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Visible FAQ prose — crawlable content for search engines and AI */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Quick Answers</span>
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-3">Annuity Questions — What Indiana Retirees Ask Most</h2>
              <p className="text-gray-600 max-w-xl mx-auto">Plain-language answers to the six questions we hear most from Indiana residents considering an annuity.</p>
            </div>
            <div className="space-y-7">
              <div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">What is an annuity and how does it work?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">An annuity is a contract between you and an insurance company. You make a lump-sum payment or a series of payments, and in return the insurer provides regular disbursements beginning either immediately or at a future date you choose. Annuities are primarily used to create a reliable, predictable income stream during retirement — one you literally cannot outlive if you choose a lifetime income rider.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">What is the difference between a fixed and indexed annuity?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">A fixed annuity pays a guaranteed interest rate on your premium — predictable and straightforward. An indexed annuity links your interest crediting to a stock market index (like the S&amp;P 500) while protecting you from market losses. With an indexed annuity you can earn more than a fixed annuity in strong market years, but your principal is never at risk if the market falls. The vast majority of annuities we place at Hoosier Life Insurance are fixed or indexed — principal-protected products.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">Are annuities tax-deferred?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Yes. Annuities grow tax-deferred, meaning you don't pay taxes on growth until you withdraw. This is especially valuable for high earners who have already maxed out other tax-advantaged accounts like 401(k)s and IRAs. There is no annual contribution limit on non-qualified annuities, making them a powerful overflow vehicle for tax-deferred savings in Indiana.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">Can I lose money in an annuity?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">With fixed and indexed annuities, your principal is protected from market losses — the insurance company absorbs any downside. With variable annuities, your account is invested in sub-accounts tied to the market and is subject to loss. We typically recommend fixed and indexed annuities for Indiana clients who want the growth potential of the market without the risk of losing what they put in.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">When can I access my annuity funds?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Most annuities have a surrender period — typically 5–10 years — during which early withdrawals may incur a surrender charge. However, most contracts allow penalty-free withdrawals of up to 10% of your account value per year even during the surrender period. After the surrender period ends, you have full access to your funds with no penalties. Many contracts also waive surrender charges for terminal illness, nursing home entry, or death.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">How much does an annuity cost in Indiana?</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Unlike life insurance, there are no monthly premiums for most annuities. You fund an annuity with a lump sum — minimums typically range from $5,000 to $25,000 depending on the carrier and product. Fixed and indexed annuities generally have no direct fees deducted from your account value; the insurance company earns its margin from the spread between what it earns on investments and what it credits to your account. We compare rates across 10+ carriers to find the best crediting rate for your situation.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Annuity FAQs</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-[#F9F7F4] rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-[#1A3A52] text-sm leading-snug">{faq.q}</span>
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-[#D97642] text-xl flex-shrink-0`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-[#4A5568] text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#1A3A52]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Secure Your Retirement Income?</h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Schedule a no-obligation strategy session to explore which annuity is right for your retirement goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-appointment" className="px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#c26535] transition-colors whitespace-nowrap">
                Book Free Strategy Call
              </Link>
              <a href="tel:3176482983" className="px-8 py-4 bg-white/10 text-white font-bold rounded-lg border border-white/30 hover:bg-white/20 transition-colors whitespace-nowrap">
                <i className="ri-phone-line mr-2" />
                (317) 648-2983
              </a>
            </div>
          </div>
        </section>

        <StarRatingDisplay rating={4.9} reviewCount={82} productName="Annuities Indiana" />
        <ServingCommunitySection productName="Annuities" productSlug="annuities" />

        {/* Related Services */}
        <section className="py-16 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#1A3A52] mb-4">Explore More Coverage Options</h2>
              <p className="text-[#4A5568]">Discover other financial solutions that complement your retirement strategy</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { to: '/term-life', icon: 'ri-calendar-check-line', title: 'Term Life Insurance', desc: 'Affordable protection for a specific period with flexible coverage' },
                { to: '/whole-life', icon: 'ri-shield-check-line', title: 'Whole Life Insurance', desc: 'Lifetime coverage with cash value growth and guaranteed benefits' },
                { to: '/indexed-universal-life', icon: 'ri-line-chart-line', title: 'Indexed Universal Life', desc: 'Market-linked growth with downside protection for wealth building' },
                { to: '/final-expense', icon: 'ri-hand-heart-line', title: 'Final Expense', desc: 'Affordable coverage for funeral costs and end-of-life expenses' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:bg-[#F9F7F4] transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] transition-colors">
                    <i className={`${item.icon} text-2xl text-[#1A3A52] group-hover:text-white`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                  <p className="text-[#4A5568] text-sm mb-4">{item.desc}</p>
                  <div className="flex items-center text-[#D97642] font-semibold text-sm">
                    Learn More <i className="ri-arrow-right-line ml-1" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <MobileProductCTABar productName="Annuities" quoteHref="/book-appointment" quoteLabel="Book Strategy Call" />
    </div>
  );
}
